import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Database, Users, Lock, Mail, Twitter, Instagram, Facebook, Github } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ThemeLogo } from '../components/ui/theme-logo';

const PrivacyPolicy = () => {
  const location = useLocation();
  
  // Force scroll to top when this route is accessed
  useEffect(() => {
    // IMMEDIATE scroll - no delays
    const immediateScroll = () => {
      // Multi-method immediate scroll
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Also scroll any potential scrollable containers immediately
      const scrollableElements = Array.from(document.querySelectorAll('*')).filter(el => {
        const style = getComputedStyle(el);
        return style.overflow === 'scroll' || style.overflow === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'auto';
      });
      
      scrollableElements.forEach(el => {
        if (el.scrollTop > 0) {
          el.scrollTop = 0;
        }
      });
    };
    
    // Execute immediately
    immediateScroll();
    
    // Backup attempts with minimal delay
    requestAnimationFrame(immediateScroll);
    setTimeout(immediateScroll, 1);
    setTimeout(immediateScroll, 10);
    
    // One final check after very short delay to ensure React rendered
    setTimeout(immediateScroll, 50);
  }, [location.pathname]); // Trigger on route change

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-white dark:from-background dark:to-gray-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-r from-background to-white dark:from-background dark:to-gray-950 shadow-md py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center">
              <ThemeLogo className="h-[42px]" />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#features" className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors">Features</Link>
              <Link to="/#how-it-works" className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors">How It Works</Link>
              <Link to="/#community" className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors">Community</Link>
              <Link to="/#pricing" className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors">Pricing</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/auth/login">
                <Button variant="ghost" className="hidden sm:flex">Sign in</Button>
              </Link>
              <Link to="/auth/login">
                <Button className="bg-primary hover:bg-primary-hover text-white">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Link to="/" className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-primary transition-colors mb-6">
              <ArrowLeft size={16} className="mr-2" />
              Back to Home
            </Link>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6"
            >
              Privacy <span className="text-primary">Policy</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto"
            >
              Your privacy is important to us. Learn how we collect, use, and protect your information.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-6 text-sm text-gray-500 dark:text-gray-500"
            >
              Last updated: Jun 11, 2025
            </motion.div>
          </div>
        </div>
      </section>

      {/* Privacy Overview */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-primary/5 rounded-2xl p-8 border border-primary/20 mb-12"
          >
            <div className="flex items-center mb-6">
              <div className="p-3 bg-primary/10 rounded-xl mr-4">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Our Privacy Commitment</h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              At Nosh, we're committed to protecting your privacy and being transparent about how we handle your data. 
              This policy explains what information we collect, how we use it, and the choices you have about your data.
            </p>
          </motion.div>

          {/* Privacy Sections */}
          <div className="space-y-12">
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center mb-6">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl mr-4">
                  <Eye className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Information We Collect</h3>
              </div>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Account Information</h4>
                  <p>When you create an account, we collect your email address, username, and password (securely hashed).</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Inventory Data</h4>
                  <p>We collect information about your food inventory, including item names, quantities, expiry dates, and photos you choose to upload.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Usage Information</h4>
                  <p>We automatically collect information about how you use our app, including features used, recipes generated, and meals tracked.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Device Information</h4>
                  <p>We may collect device-specific information such as your device type, operating system, and browser type to optimize our services.</p>
                </div>
              </div>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center mb-6">
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl mr-4">
                  <Database className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">How We Use Your Information</h3>
              </div>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Service Provision</h4>
                  <p>We use your information to provide and improve our food waste reduction services, including personalized recipe suggestions and inventory management.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">AI Features</h4>
                  <p>Your inventory and preference data helps our AI generate relevant recipes and meal suggestions tailored to your needs.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Communication</h4>
                  <p>We may use your email to send important updates about your account, new features, and tips for reducing food waste.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Analytics & Improvement</h4>
                  <p>We analyze usage patterns to improve our app's functionality and develop new features that better serve our users.</p>
                </div>
              </div>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center mb-6">
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl mr-4">
                  <Users className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Information Sharing</h3>
              </div>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <p>We don't sell, rent, or share your personal information with third parties for their marketing purposes. We may share information in these limited circumstances:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>With service providers who help us operate our app (all bound by strict confidentiality agreements)</li>
                  <li>When required by law or to protect our rights and safety</li>
                  <li>In the event of a business merger or acquisition (with notice to users)</li>
                  <li>With your explicit consent for specific purposes</li>
                </ul>
              </div>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center mb-6">
                <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-xl mr-4">
                  <Lock className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Data Security</h3>
              </div>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <p>We implement industry-standard security measures to protect your information:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>All data is encrypted in transit and at rest</li>
                  <li>Secure authentication and password hashing</li>
                  <li>Regular security audits and monitoring</li>
                  <li>Limited access to personal data on a need-to-know basis</li>
                  <li>Secure cloud infrastructure with regular backups</li>
                </ul>
                <p>While we use best practices to protect your data, no method of transmission or storage is 100% secure. We encourage you to use strong passwords and keep your account information confidential.</p>
              </div>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Your Rights and Choices</h3>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <p>You have the following rights regarding your personal information:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Access:</strong> Request a copy of the personal information we have about you</li>
                  <li><strong>Correction:</strong> Update or correct inaccurate information in your account</li>
                  <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
                  <li><strong>Portability:</strong> Export your data in a machine-readable format</li>
                  <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
                </ul>
                <p>To exercise these rights, please contact us at privacy@nosh.app or through your account settings.</p>
              </div>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Contact Us</h3>
              <div className="text-gray-600 dark:text-gray-400 space-y-4">
                <p>If you have questions about this Privacy Policy or our data practices, please contact us:</p>
                <div className="space-y-2">
                  <p><strong>Email:</strong> privacy@nosh.app</p>
                  <p><strong>Address:</strong> 123 Food Tech Avenue, San Francisco, CA 94103</p>
                </div>
                <p>We'll respond to your inquiry within 30 days.</p>
              </div>
            </motion.section>
          </div>

          {/* Changes to Policy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-12 bg-amber-50 dark:bg-amber-900/10 rounded-2xl p-8 border border-amber-200 dark:border-amber-800"
          >
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Changes to This Policy</h3>
            <p className="text-gray-600 dark:text-gray-400">
              We may update this Privacy Policy from time to time. We'll notify you of any material changes by email 
              or through our app. Your continued use of our services after such changes constitutes acceptance of the updated policy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center mb-4">
                <img src="/nosh-new-wh.png" alt="Nosh" className="h-[42px]" />
              </Link>
              <p className="text-gray-400 mb-6 max-w-md">
                Reduce food waste with AI-powered inventory management, recipe suggestions, and meal tracking.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Twitter size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Instagram size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Facebook size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Github size={20} />
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/#features" className="text-gray-400 hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/#how-it-works" className="text-gray-400 hover:text-white transition-colors">How It Works</Link></li>
                <li><Link to="/#pricing" className="text-gray-400 hover:text-white transition-colors">Pricing</Link></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">API</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><Link to="/help-center" className="text-gray-400 hover:text-white transition-colors">Help Center</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact Us</Link></li>
                <li><Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2025 Nosh. All rights reserved.
            </p>
            <div className="flex items-center mt-4 md:mt-0">
              <Mail size={16} className="mr-2 text-gray-400" />
              <a href="mailto:hello@noshhq.com" className="text-gray-400 hover:text-white transition-colors text-sm">
                hello@noshhq.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;